/**
 * QuePasa Inbox Helper Functions
 * Centralized logic for identifying and working with QuePasa inboxes
 */

/**
 * Resolve provider from different payload shapes returned by API/store.
 * @param {Object} inbox - The inbox object
 * @returns {string|null}
 */
export const getInboxProvider = inbox => {
  if (!inbox) return null;

  // Historical QuePasa pattern: API subtype is exposed via medium='quepasa'.
  // Keep this fallback to stay compatible with older cache/API shapes.
  if (inbox.channel_type === 'Channel::Api' && inbox.medium) {
    return inbox.medium;
  }

  return (
    inbox.additional_attributes?.provider ||
    inbox.provider ||
    inbox.channel?.provider ||
    inbox.channel?.additional_attributes?.provider ||
    null
  );
};

/**
 * Check if an inbox is a QuePasa inbox
 * @param {Object} inbox - The inbox object to check
 * @returns {boolean} - True if the inbox is a QuePasa inbox
 */
export const isQuePasaInbox = inbox => {
  if (!inbox) return false;

  // Check channel type and provider
  const isApiChannel = inbox.channel_type === 'Channel::Api';
  const isQuePasaProvider = getInboxProvider(inbox) === 'quepasa';

  return isApiChannel && isQuePasaProvider;
};

/**
 * Get QuePasa-specific additional attributes
 * @param {Object} inbox - The inbox object
 * @returns {Object} - QuePasa additional attributes or empty object
 */
export const getQuePasaAttributes = inbox => {
  if (!isQuePasaInbox(inbox)) return {};
  return inbox.additional_attributes || {};
};

/**
 * Check if inbox has QR code support
 * @param {Object} inbox - The inbox object
 * @returns {boolean} - True if supports QR code
 */
export const hasQrCodeSupport = inbox => {
  if (!isQuePasaInbox(inbox)) return false;
  const attrs = getQuePasaAttributes(inbox);
  return attrs.qrcode_url !== undefined;
};
